/**
 * Authentication Context
 * 
 * Provides authentication state and methods throughout the app.
 * Uses Firebase Auth with email/password authentication.
 * Includes user profile with admin and approval status from Firestore.
 */

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { getUserProfile, createUserProfile } from '../services/users';

// Create the authentication context
const AuthContext = createContext();

/**
 * Custom hook to access auth context
 * @returns {Object} Auth context with user, loading, login, register, logout
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Authentication Provider Component
 * Wraps the app and provides auth state to all children
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const pendingRegistrationProfileRef = useRef(null);

  // Listen for auth state changes and fetch user profile
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        try {
          // Fetch or create user profile
          let profile = await getUserProfile(firebaseUser.uid);
          if (!profile) {
            profile = await createUserProfile(
              firebaseUser.uid,
              firebaseUser.email,
              pendingRegistrationProfileRef.current || {}
            );
          }
          pendingRegistrationProfileRef.current = null;
          setUserProfile(profile);
        } catch (error) {
          console.error('Error fetching user profile:', error);
          setUserProfile({ isAdmin: false, status: 'pending' });
        }
      } else {
        setUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return unsubscribe;
  }, []);

  /**
   * Sign in with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   */
  const login = async (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  /**
   * Create new account with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @param {Object} [profileData] - Optional profile data
   * @param {string} [profileData.voornaam] - User first name
   * @param {string} [profileData.achternaam] - User last name
   */
  const register = async (email, password, profileData = {}) => {
    pendingRegistrationProfileRef.current = {
      voornaam: (profileData.voornaam || '').trim(),
      achternaam: (profileData.achternaam || '').trim()
    };

    const credential = await createUserWithEmailAndPassword(auth, email, password);

    // Persist name fields immediately after account creation.
    await createUserProfile(credential.user.uid, credential.user.email, pendingRegistrationProfileRef.current);
    return credential;
  };

  /**
   * Send password reset email
   * @param {string} email - User email
   */
  const resetPassword = async (email) => {
    return sendPasswordResetEmail(auth, email);
  };

  /**
   * Sign out the current user
   */
  const logout = async () => {
    return signOut(auth);
  };

  /**
   * Refresh the user profile from Firestore
   * Used after approval status changes
   */
  const refreshProfile = async () => {
    if (user) {
      try {
        const profile = await getUserProfile(user.uid);
        setUserProfile(profile);
      } catch (error) {
        console.error('Error refreshing user profile:', error);
      }
    }
  };

  const value = {
    user,
    userProfile,
    isAdmin: userProfile?.isAdmin || false,
    isApproved: userProfile?.status === 'approved',
    isRejected: userProfile?.status === 'rejected',
    isPending: userProfile?.status === 'pending',
    loading,
    login,
    register,
    resetPassword,
    logout,
    refreshProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
